import { ApiErrorSchema, type ErrorCode } from '@siteday/shared';
import type { z } from 'zod';

import { runtimeEnv } from '@/lib/observability';

/**
 * Base URL for the API (doc 05: /api/v1). Runtime `env.js` (Docker image) wins over
 * the build-time VITE_API_URL; in mock mode MSW intercepts the same origin.
 */
export const API_URL: string = (
  runtimeEnv().API_URL || import.meta.env.VITE_API_URL || 'http://localhost:4000/api/v1'
).replace(/\/$/, '');
export const IS_MOCK: boolean = import.meta.env.VITE_MOCK === '1';

export type ClientErrorCode = ErrorCode | 'NETWORK' | 'BAD_RESPONSE';

/** Every API error body is { error: { code, message, details? } } (doc 05). */
export class ApiError extends Error {
  readonly code: ClientErrorCode;
  readonly status: number;
  readonly details: unknown[] | undefined;
  constructor(code: ClientErrorCode, message: string, status: number, details?: unknown[]) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.status = status;
    this.details = details;
  }
  is(code: ClientErrorCode): boolean {
    return this.code === code;
  }
}

export function isApiError(e: unknown): e is ApiError {
  return e instanceof ApiError;
}

export function errorMessage(e: unknown): string {
  if (isApiError(e)) return e.message;
  if (e instanceof Error) return e.message;
  return 'Something went wrong';
}

type Query = Record<string, string | number | boolean | null | undefined>;

export interface RequestOptions<T> {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  query?: Query;
  schema?: z.ZodType<T, z.ZodTypeDef, unknown>;
  form?: FormData;
}

const connectionListeners = new Set<(online: boolean) => void>();
/** The connection-lost bar subscribes here; the client reports network failures and recoveries. */
export function onConnectionChange(fn: (online: boolean) => void): () => void {
  connectionListeners.add(fn);
  return () => connectionListeners.delete(fn);
}
function reportConnection(online: boolean) {
  for (const fn of connectionListeners) fn(online);
}

export function buildUrl(path: string, query?: Query): string {
  const url = new URL(`${API_URL}${path.startsWith('/') ? path : `/${path}`}`);
  if (query) {
    for (const [k, v] of Object.entries(query)) {
      if (v === undefined || v === null || v === '') continue;
      url.searchParams.set(k, String(v));
    }
  }
  return url.toString();
}

/**
 * fetch wrapper: credentials included (httpOnly cookies, doc 11), JSON in/out,
 * response parsed with the shared Zod schema so the contract is enforced at the edge.
 */
export async function request<T = unknown>(path: string, opts: RequestOptions<T> = {}): Promise<T> {
  const { method = 'GET', body, query, schema, form } = opts;
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (body !== undefined) headers['Content-Type'] = 'application/json';

  let res: Response;
  try {
    const init: RequestInit = { method, credentials: 'include', headers };
    if (form) init.body = form;
    else if (body !== undefined) init.body = JSON.stringify(body);
    res = await fetch(buildUrl(path, query), init);
  } catch {
    reportConnection(false);
    throw new ApiError('NETWORK', 'Connection lost. Check your network and try again.', 0);
  }
  reportConnection(true);

  if (res.status === 204) return undefined as T;

  const text = await res.text();
  let json: unknown = null;
  if (text) {
    try {
      json = JSON.parse(text);
    } catch {
      json = null;
    }
  }

  if (!res.ok) {
    const parsed = ApiErrorSchema.safeParse(json);
    if (parsed.success) {
      const { code, message, details } = parsed.data.error;
      throw new ApiError(code, message, res.status, details);
    }
    throw new ApiError(
      res.status === 401 ? 'UNAUTHENTICATED' : 'INTERNAL',
      `Request failed (${res.status})`,
      res.status,
    );
  }

  if (!schema) return json as T;
  const out = schema.safeParse(json);
  if (!out.success) {
    if (import.meta.env.DEV)
      console.error('Response did not match contract', path, out.error.flatten());
    throw new ApiError('BAD_RESPONSE', `Unexpected response from ${path}`, res.status);
  }
  return out.data;
}

export const api = {
  get: <T>(path: string, schema?: z.ZodType<T, z.ZodTypeDef, unknown>, query?: Query) =>
    request<T>(path, schema ? { schema, ...(query ? { query } : {}) } : query ? { query } : {}),
  post: <T>(path: string, body?: unknown, schema?: z.ZodType<T, z.ZodTypeDef, unknown>) =>
    request<T>(path, {
      method: 'POST',
      ...(body !== undefined ? { body } : {}),
      ...(schema ? { schema } : {}),
    }),
  patch: <T>(path: string, body: unknown, schema?: z.ZodType<T, z.ZodTypeDef, unknown>) =>
    request<T>(path, { method: 'PATCH', body, ...(schema ? { schema } : {}) }),
  put: <T>(path: string, body: unknown, schema?: z.ZodType<T, z.ZodTypeDef, unknown>) =>
    request<T>(path, { method: 'PUT', body, ...(schema ? { schema } : {}) }),
  del: <T>(path: string, schema?: z.ZodType<T, z.ZodTypeDef, unknown>) =>
    request<T>(path, { method: 'DELETE', ...(schema ? { schema } : {}) }),
};
